import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  TrashIcon,
  ShoppingBagIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  PlusIcon,
  MinusIcon,
} from "@heroicons/react/24/outline";
import { useCart } from "../../contexts/CartContext";
import { getFunctions, httpsCallable } from "firebase/functions";

interface ShoppingCartProps {
  onClose: () => void;
}

interface ShippingDetails {
  name: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

const initialShippingDetails: ShippingDetails = {
  name: "",
  email: "",
  phone: "",
  street: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",
};

const ShoppingCart: React.FC<ShoppingCartProps> = ({ onClose }) => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } =
    useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [shippingDetails, setShippingDetails] = useState<ShippingDetails>(
    initialShippingDetails
  );
  const [shippingErrors, setShippingErrors] = useState<
    Partial<Record<keyof ShippingDetails, string>>
  >({});

  const steps = ["Cart Review", "Shipping Details", "Checkout"];

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity >= 1) {
      updateQuantity(itemId, newQuantity);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    removeFromCart(itemId);
  };

  const handleShippingDetailsChange =
    (field: keyof ShippingDetails) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setShippingDetails({
        ...shippingDetails,
        [field]: event.target.value,
      });

      // Clear error when field is modified
      if (shippingErrors[field]) {
        setShippingErrors({
          ...shippingErrors,
          [field]: "",
        });
      }
    };

  const validateShippingDetails = (): boolean => {
    const errors: Partial<Record<keyof ShippingDetails, string>> = {};
    let isValid = true;

    // Required fields
    const requiredFields: Array<keyof ShippingDetails> = [
      "name",
      "email",
      "street",
      "city",
      "state",
      "zipCode",
      "country",
    ];

    requiredFields.forEach((field) => {
      if (!shippingDetails[field].trim()) {
        errors[field] = "This field is required";
        isValid = false;
      }
    });

    // Email validation
    if (shippingDetails.email && !/\S+@\S+\.\S+/.test(shippingDetails.email)) {
      errors.email = "Please enter a valid email address";
      isValid = false;
    }

    setShippingErrors(errors);
    return isValid;
  };

  const handleNext = () => {
    if (activeStep === 1) {
      // Validate shipping details before proceeding
      if (!validateShippingDetails()) {
        return;
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
    if (activeStep === 2) {
      handleCheckout();
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;

    try {
      setIsCheckingOut(true);
      setError(null);

      // Create line items for Stripe
      const lineItems = cart.map((cartItem) => ({
        productId: cartItem.item.id,
        quantity: cartItem.quantity,
        price: parseFloat(cartItem.item.price) * 100, // Convert to cents
        name: cartItem.item.name,
        description: cartItem.item.description,
        imageUrl: cartItem.item.imageURL,
      }));

      console.log("Creating checkout with items:", lineItems);

      // Call Firebase Function to create checkout session
      const functions = getFunctions();
      const createCartCheckoutSession = httpsCallable(
        functions,
        "createCartCheckoutSession"
      );

      const result = await createCartCheckoutSession({
        items: lineItems,
        success_url: window.location.origin + "/shop?checkout=success",
        cancel_url: window.location.origin + "/shop?checkout=canceled",
        shipping: {
          name: shippingDetails.name,
          email: shippingDetails.email,
          phone: shippingDetails.phone,
          address: {
            street: shippingDetails.street,
            city: shippingDetails.city,
            state: shippingDetails.state,
            postal_code: shippingDetails.zipCode,
            country: shippingDetails.country,
          },
        },
        metadata: {
          orderType: "shop_order", // Flag to identify this as a shop order
          cartItems: JSON.stringify(
            cart.map((item) => ({
              id: item.item.id,
              name: item.item.name,
              quantity: item.quantity,
            }))
          ),
        },
      });

      // Redirect to Stripe checkout
      const data = result.data as { url: string };
      if (data.url) {
        console.log("Redirecting to checkout URL:", data.url);
        // Clear cart after successful checkout initiation
        clearCart();
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned from Stripe");
      }
    } catch (err: any) {
      console.error("Failed to create checkout session:", err);
      let errorMessage = "Failed to create checkout session. Please try again.";

      // Extract more detailed error message if available
      if (err.message) {
        errorMessage += " Error: " + err.message;
      }

      // Check for Firebase function error details
      if (err.details) {
        console.error("Error details:", err.details);
        if (typeof err.details === "string") {
          errorMessage += " " + err.details;
        } else if (err.details.message) {
          errorMessage += " " + err.details.message;
        }
      }

      setError(errorMessage);
      setActiveStep(0); // Reset to cart view on error
    } finally {
      setIsCheckingOut(false);
    }
  };

  // Render Cart Contents
  const renderCartContents = () => (
    <>
      <div className="flex-grow overflow-auto">
        {cart.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {cart.map((cartItem) => (
              <li key={cartItem.item.id} className="py-4 px-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden bg-gray-100">
                    {cartItem.item.imageURL ? (
                      <img
                        src={cartItem.item.imageURL}
                        alt={cartItem.item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <ShoppingBagIcon className="h-8 w-8" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {cartItem.item.name}
                    </h4>
                    <p className="mt-1 text-sm text-gray-500">
                      ${cartItem.item.price} each
                    </p>

                    <div className="mt-2 flex items-center">
                      <span className="text-sm text-gray-500 mr-2">Qty:</span>
                      <div className="flex items-center border border-gray-300 rounded-md">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(cartItem.item.id, cartItem.quantity - 1)}
                          className="p-1 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gold"
                          disabled={cartItem.quantity <= 1}
                        >
                          <MinusIcon className="h-4 w-4 text-gray-600" />
                        </button>
                        <span className="px-3 py-1 text-sm font-medium text-gray-900 min-w-[2rem] text-center">
                          {cartItem.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(cartItem.item.id, cartItem.quantity + 1)}
                          className="p-1 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gold"
                        >
                          <PlusIcon className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(cartItem.item.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <ShoppingBagIcon className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">
              Your cart is empty
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Add items to get started
            </p>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 p-4 bg-gray-50">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Subtotal ({cart.reduce((total, item) => total + item.quantity, 0)}{" "}
            items):
          </span>
          <span className="text-sm font-bold text-gray-900">
            ${getCartTotal().toFixed(2)}
          </span>
        </div>

        <button
          className="w-full bg-gold hover:bg-opacity-90 text-white py-2 px-4 rounded-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed mb-2"
          onClick={handleNext}
          disabled={isCheckingOut || cart.length === 0}
        >
          Continue to Shipping
          <ArrowRightIcon className="ml-2 h-4 w-4" />
        </button>

        <button
          className="w-full text-red-600 hover:text-red-800 text-sm py-1 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={clearCart}
          disabled={isCheckingOut || cart.length === 0}
        >
          Clear Cart
        </button>
      </div>
    </>
  );

  // Render Shipping Form
  const renderShippingForm = () => (
    <div className="p-4 flex flex-col h-full">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Shipping Information
      </h3>

      <div className="flex-grow overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Full Name *
            </label>
            <input
              id="name"
              type="text"
              value={shippingDetails.name}
              onChange={handleShippingDetailsChange("name")}
              className={`w-full px-3 py-2 border ${
                shippingErrors.name ? "border-red-300" : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold`}
            />
            {shippingErrors.name && (
              <p className="mt-1 text-sm text-red-600">{shippingErrors.name}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email *
            </label>
            <input
              id="email"
              type="email"
              value={shippingDetails.email}
              onChange={handleShippingDetailsChange("email")}
              className={`w-full px-3 py-2 border ${
                shippingErrors.email ? "border-red-300" : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold`}
            />
            {shippingErrors.email && (
              <p className="mt-1 text-sm text-red-600">
                {shippingErrors.email}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone Number
            </label>
            <input
              id="phone"
              type="tel"
              value={shippingDetails.phone}
              onChange={handleShippingDetailsChange("phone")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold"
            />
          </div>

          <div className="md:col-span-2">
            <label
              htmlFor="street"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Street Address *
            </label>
            <input
              id="street"
              type="text"
              value={shippingDetails.street}
              onChange={handleShippingDetailsChange("street")}
              className={`w-full px-3 py-2 border ${
                shippingErrors.street ? "border-red-300" : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold`}
            />
            {shippingErrors.street && (
              <p className="mt-1 text-sm text-red-600">
                {shippingErrors.street}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="city"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              City *
            </label>
            <input
              id="city"
              type="text"
              value={shippingDetails.city}
              onChange={handleShippingDetailsChange("city")}
              className={`w-full px-3 py-2 border ${
                shippingErrors.city ? "border-red-300" : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold`}
            />
            {shippingErrors.city && (
              <p className="mt-1 text-sm text-red-600">{shippingErrors.city}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="state"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              State/Province *
            </label>
            <input
              id="state"
              type="text"
              value={shippingDetails.state}
              onChange={handleShippingDetailsChange("state")}
              className={`w-full px-3 py-2 border ${
                shippingErrors.state ? "border-red-300" : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold`}
            />
            {shippingErrors.state && (
              <p className="mt-1 text-sm text-red-600">
                {shippingErrors.state}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="zipCode"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              ZIP / Postal Code *
            </label>
            <input
              id="zipCode"
              type="text"
              value={shippingDetails.zipCode}
              onChange={handleShippingDetailsChange("zipCode")}
              className={`w-full px-3 py-2 border ${
                shippingErrors.zipCode ? "border-red-300" : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold`}
            />
            {shippingErrors.zipCode && (
              <p className="mt-1 text-sm text-red-600">
                {shippingErrors.zipCode}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="country"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Country *
            </label>
            <input
              id="country"
              type="text"
              value={shippingDetails.country}
              onChange={handleShippingDetailsChange("country")}
              className={`w-full px-3 py-2 border ${
                shippingErrors.country ? "border-red-300" : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold`}
            />
            {shippingErrors.country && (
              <p className="mt-1 text-sm text-red-600">
                {shippingErrors.country}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-4 mt-4 flex justify-between">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Cart
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
        >
          Continue to Checkout
          <ArrowRightIcon className="h-5 w-5 ml-2" />
        </button>
      </div>
    </div>
  );

  // Render Order Summary
  const renderOrderSummary = () => (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">Order Summary</h2>

      <div className="mb-8">
        <h3 className="text-lg font-medium mb-2">Items:</h3>
        {cart.map((item) => (
          <div key={item.item.id} className="flex justify-between mb-2">
            <p className="text-sm">
              {item.quantity} x {item.item.name}
            </p>
            <p className="text-sm">
              ${(parseFloat(item.item.price) * item.quantity).toFixed(2)}
            </p>
          </div>
        ))}
        <div className="flex justify-between mb-2">
          <p className="text-sm">Shipping:</p>
          <p className="text-sm">$10.00</p>
        </div>

        <div className="border-t border-gray-200 my-4"></div>
        <div className="flex justify-between">
          <p className="text-base font-bold">Total:</p>
          <p className="text-base font-bold">
            ${(getCartTotal() + 10).toFixed(2)}
          </p>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-lg font-medium mb-2">Shipping to:</h3>
        <p className="text-sm">{shippingDetails.name}</p>
        <p className="text-sm">{shippingDetails.street}</p>
        <p className="text-sm">
          {shippingDetails.city}, {shippingDetails.state}{" "}
          {shippingDetails.zipCode}
        </p>
        <p className="text-sm">{shippingDetails.country}</p>
        <p className="text-sm mt-2">{shippingDetails.email}</p>
        {shippingDetails.phone && (
          <p className="text-sm">{shippingDetails.phone}</p>
        )}
      </div>

      <div className="flex justify-between">
        <button
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
          onClick={handleBack}
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Shipping
        </button>
        <button
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gold hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold disabled:opacity-50"
          disabled={isCheckingOut}
          onClick={handleCheckout}
        >
          {isCheckingOut ? (
            <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
          ) : (
            "Place Order"
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 flex items-center justify-between border-b border-gray-200">
        <h2 className="text-xl font-semibold">Your Cart</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {cart.length === 0 ? (
        <div className="flex-grow flex flex-col items-center justify-center p-6">
          <div className="relative mb-4">
            <ShoppingBagIcon className="h-16 w-16 text-gray-300" />
            <div className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs rounded-full">
              0
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-500 text-center">
            Your cart is empty
          </h3>
          <p className="mt-2 text-sm text-gray-400 text-center">
            Add some products to your cart and they will appear here
          </p>
          <button
            className="mt-6 px-4 py-2 bg-gold text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
            onClick={onClose}
          >
            Continue Shopping
          </button>
        </div>
      ) : (
        <>
          <div className="p-6">
            <div className="mb-6">
              <ol className="flex items-center w-full">
                {steps.map((step, index) => (
                  <li
                    key={step}
                    className={`flex items-center ${
                      index < steps.length - 1 ? "w-full" : ""
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        index < activeStep
                          ? "bg-gold text-white"
                          : index === activeStep
                          ? "border-2 border-gold text-gold"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {index + 1}
                    </div>
                    <span className="ml-2 text-sm font-medium hidden sm:inline">
                      {step}
                    </span>
                    {index < steps.length - 1 && (
                      <div className="w-full flex items-center">
                        <div
                          className={`flex-1 h-0.5 mx-4 ${
                            index < activeStep ? "bg-gold" : "bg-gray-200"
                          }`}
                        ></div>
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>

            <div className="flex-grow flex flex-col overflow-auto">
              {activeStep === 0 && renderCartContents()}
              {activeStep === 1 && renderShippingForm()}
              {activeStep === 2 && renderOrderSummary()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ShoppingCart;
